import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";

/**
 * SubscriptionContext - Single source of truth for subscription state
 * 
 * This context centralizes all subscription data fetching to prevent:
 * - Stale UI from independent fetches
 * - Race conditions after webhook updates
 * - "I paid but still blocked" issues
 * - Inconsistent route gating
 * 
 * All components MUST use this context instead of fetching directly.
 */

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

// Allowed subscription statuses for app access
const ALLOWED_STATUSES = ["trialing", "intro", "active"];

export const SubscriptionProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  
  // Core subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  
  // Polling state
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 15;
  const POLL_INTERVAL = 2000;

  /**
   * Fetch subscription status from API
   * This is the ONLY place that should call /api/subscription/status
   */
  const fetchSubscriptionStatus = useCallback(async (options = {}) => {
    const { silent = false } = options;
    
    if (!token) {
      setSubscriptionStatus(null);
      setIsLoading(false);
      return null;
    }

    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      const data = response.data;
      setSubscriptionStatus(data);
      setLastFetchedAt(new Date().toISOString());
      return data;
    } catch (err) {
      console.error("Failed to fetch subscription status:", err);
      setError(err.message || "Failed to fetch subscription status");
      // Set inactive status on error
      setSubscriptionStatus({ subscription_status: "inactive" });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  /**
   * Refresh subscription status
   * Call this after any action that might change subscription state
   * Now syncs from Stripe first to ensure we have latest billing info
   */
  const refreshSubscription = useCallback(async () => {
    // Sync from Stripe first to get latest billing cycle info
    if (token) {
      try {
        await axios.post(`${API}/subscription/sync`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Ignore sync errors, continue with normal fetch
        console.warn('Subscription sync failed, using cached data');
      }
    }
    return fetchSubscriptionStatus({ silent: true });
  }, [fetchSubscriptionStatus, token]);

  /**
   * Start polling for subscription status
   * Used after checkout to wait for webhook confirmation
   * Now includes fallback sync from Stripe if webhook is delayed
   */
  const startPolling = useCallback((onSuccess, onTimeout) => {
    // Clear any existing polling
    stopPolling();
    pollCountRef.current = 0;

    const poll = async () => {
      pollCountRef.current += 1;
      
      // Every 3rd poll, try to sync directly from Stripe (fallback for webhook delay)
      if (pollCountRef.current % 3 === 0 && token) {
        try {
          await axios.post(`${API}/subscription/sync`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          // Ignore sync errors, continue with normal polling
        }
      }
      
      const data = await fetchSubscriptionStatus({ silent: true });
      
      if (data) {
        const status = data.subscription_status;
        // Check if subscription is now active
        if (ALLOWED_STATUSES.includes(status) || status === "past_due") {
          stopPolling();
          onSuccess?.(data);
          return;
        }
      }

      // Check if we've exceeded max attempts
      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        stopPolling();
        onTimeout?.();
        return;
      }

      // Continue polling
      pollingRef.current = setTimeout(poll, POLL_INTERVAL);
    };

    // Start first poll
    poll();
  }, [fetchSubscriptionStatus, token]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  /**
   * Check if user has access to protected routes
   */
  const hasAccess = useCallback(() => {
    if (!subscriptionStatus) return false;
    
    const status = subscriptionStatus.subscription_status;
    
    // Check allowed statuses
    if (ALLOWED_STATUSES.includes(status)) {
      return true;
    }
    
    // For past_due, check grace period
    if (status === "past_due") {
      return !subscriptionStatus.past_due_grace_expired;
    }
    
    return false;
  }, [subscriptionStatus]);

  /**
   * Get computed properties from subscription status
   */
  const getComputedProps = useCallback(() => {
    if (!subscriptionStatus) {
      return {
        plan: null,
        planName: null,
        planPrice: null,
        billingCadence: null,
        trialEndDate: null,
        currentPeriodEnd: null,
        cancelAtDate: null,
        graceExpiresAt: null,
        isTrialing: false,
        isActive: false,
        isPastDue: false,
        isInactive: true,
        isCanceling: false,
        leadsUsed: 0,
        leadsLimit: 0,
        formsCount: 0,
        formsLimit: 0,
      };
    }

    const status = subscriptionStatus.subscription_status;
    
    return {
      plan: subscriptionStatus.subscription_plan,
      planName: subscriptionStatus.plan_name,
      planPrice: subscriptionStatus.plan_price,
      billingCadence: subscriptionStatus.billing_cadence,
      trialEndDate: subscriptionStatus.intro_end_at,
      currentPeriodEnd: subscriptionStatus.cycle_end_at || subscriptionStatus.next_billing_date,
      cancelAtDate: subscriptionStatus.cancel_at_date,
      graceExpiresAt: subscriptionStatus.past_due_since 
        ? new Date(new Date(subscriptionStatus.past_due_since).getTime() + 48 * 60 * 60 * 1000).toISOString()
        : null,
      isTrialing: status === "intro" || status === "trialing",
      isActive: status === "active",
      isPastDue: status === "past_due",
      isInactive: status === "inactive" || status === "canceled",
      isCanceling: subscriptionStatus.cancel_at_period_end === true,
      leadsUsed: subscriptionStatus.is_trialing 
        ? subscriptionStatus.trial_leads_used 
        : subscriptionStatus.leads_used,
      leadsLimit: subscriptionStatus.is_trialing 
        ? subscriptionStatus.trial_leads_limit 
        : subscriptionStatus.leads_limit,
      formsCount: subscriptionStatus.forms_count,
      formsLimit: subscriptionStatus.forms_limit,
      pastDueHoursRemaining: subscriptionStatus.past_due_hours_remaining,
      pastDueGraceExpired: subscriptionStatus.past_due_grace_expired,
      trialDaysRemaining: subscriptionStatus.intro_days_remaining || subscriptionStatus.trial_days_remaining,
      hasUpgradedEarly: subscriptionStatus.has_upgraded_early,
      trialLimitReached: subscriptionStatus.trial_limit_reached,
      stripeSubscriptionId: subscriptionStatus.stripe_subscription_id,
      pendingDowngrade: subscriptionStatus.pending_downgrade,
      pendingDowngradeTo: subscriptionStatus.pending_downgrade_to,
    };
  }, [subscriptionStatus]);

  // Initial fetch when auth is ready
  useEffect(() => {
    if (!authLoading) {
      if (token && user) {
        fetchSubscriptionStatus();
      } else {
        setSubscriptionStatus(null);
        setIsLoading(false);
      }
    }
  }, [authLoading, token, user, fetchSubscriptionStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const value = {
    // Raw subscription data
    subscriptionStatus,
    
    // Loading and error states
    isLoading,
    error,
    lastFetchedAt,
    
    // Computed properties
    ...getComputedProps(),
    
    // Access check
    hasAccess: hasAccess(),
    
    // Actions
    refreshSubscription,
    startPolling,
    stopPolling,
    
    // Raw status for components that need it
    status: subscriptionStatus?.subscription_status,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
